import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { UserService } from 'src/app/services/user/user.service';
import { UpdateByService } from 'src/app/services/user/update-by.service';
import { AccountsListService } from 'src/app/services/accounts/accounts-list.service';
import { AccountService } from 'src/app/services/account/account.service';
import { PopupService } from 'src/app/services/general/popup.service';
import { ErrorService } from 'src/app/services/general/error.service';

import { LoginUrl, LoginCredential, LoginResult } from 'src/app/interfaces/login';
import { UpdateBy } from 'src/app/interfaces/user';
import { UserAccounts } from 'src/app/interfaces/account';
import { ShareMethod } from 'src/app/types/share';
import { FunctionService } from 'src/app/services/general/function.service';
import { UserRtdbService } from 'src/app/services/user/user-rtdb.service';
import { AnalyticsService } from 'src/app/services/general/analytics.service';

/**
 * Account login page component
 */
@Component({
  selector: 'app-accounts-login',
  templateUrl: './accounts-login.page.html',
  styleUrls: ['./accounts-login.page.scss'],
})
export class AccountsLoginPage implements OnInit, OnDestroy {

  ready: boolean;
  private loginUrl: LoginUrl;

  /**
   * Constructor
   * @param route Route
   * @param router Router
   * @param translate Translate
   * @param fns Angularfire Cloud Function
   * @param userService User Service
   * @param accountsListService Accounts List Service
   * @param accountService Account Service
   * @param popupService Popup Service
   * @param errorService Error Service
   */
  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private fns: AngularFireFunctions,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private userRtdbService: UserRtdbService,
    private updateByService: UpdateByService,
    private accountsListService: AccountsListService,
    private accountService: AccountService,
    private functionService: FunctionService,
    private popupService: PopupService,
    private errorService: ErrorService,
  ) { }

  /**
   * Ng on init
   */
  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
  }

  ionViewWillEnter() {
    this.ready = true;
  }

  ionViewDidLeave() {
    this.ready = false;
  }

  /**
   * Watch route
   */
  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;
      if (state?.loginUrl) {
        this.loginUrl = state.loginUrl;
        if (this.loginUrl?.login?.weddingId && this.loginUrl?.login?.weddingPass) {
          this.validateAccountLogin(this.loginUrl.login, state?.loginMethod);
        } else {
          this.promptError();
        }
      } else if (state?.accountId && state?.new) {
        this.setupAccount(state.accountId);
      } else {
        this.promptError();
      }
    });
  }

  /**
   * Validate account login
   * @param weddingId Wedding ID
   * @param weddingPass Wedding Password
   * @param type Type
   * @param time Login Generated Time
   */
  async validateAccountLogin(login: LoginCredential, loginMethod: ShareMethod) {
    if (login?.weddingId && login?.weddingPass) {
      if (this.userService.user) {
        // const diff = this.functionService.firestoreLocalTime.seconds - Number(timestamp);
        // if (type === 'url') {
        //   if (diff > 259500) {
        //     this.promptError(this.translate.instant('ACCOUNTS_LOGIN.url_expired'));
        //   }
        // } else if (type === 'qrcode') {
        //   if (diff > 330) {
        //     this.promptError(this.translate.instant('ACCOUNTS_LOGIN.qrcode_expired'));
        //   }
        // } else if (type === 'password') {

        // } else {
        //   this.promptError();
        // }
        if (login.weddingId.length >= 6 && login.weddingId.length <= 8 && login.weddingPass.length >= 6 && login.weddingPass.length <= 8) {
          await this.checkAccountLogin(login.weddingId, login.weddingPass, loginMethod);
        } else {
          this.promptError();
        }
      } else {
        this.promptError();
      }
    } else {
      this.promptError();
    }
  }

  /**
   * Check Account Login
   * @param weddingId Wedding ID
   * @param weddingPass Wedding Password
   * @param passwordLogin password login flag
   */
  async checkAccountLogin(weddingId: string, weddingPass: string, loginMethod: ShareMethod) {
    let codeExisted = false;
    let passwordExisted = false;
    let accountDisable = false;
    let accountId = '';

    const accountUserInfo = this.accountsListService.userAccountsList;
    if (accountUserInfo) {
      const weddingIdIndex = accountUserInfo?.findIndex(
        (x: UserAccounts) => x?.login?.weddingId === weddingId && x?.enable);
      if (weddingIdIndex !== -1) {
        codeExisted = true;
        accountId = accountUserInfo[weddingIdIndex].accountId;

        if (!accountUserInfo[weddingIdIndex].enable) {
          accountDisable = true;
        }

        const weddingPassIndex = accountUserInfo?.findIndex(
          (x: UserAccounts) => x?.login?.weddingId === weddingId && x?.login?.weddingPass === weddingPass && x?.enable);

        if (weddingPassIndex !== -1) {
          passwordExisted = true;
        }
      }
    }

    if (codeExisted && passwordExisted && accountId) {
      if (accountDisable) {
        this.promptError(this.translate.instant('ACCOUNTS_LOGIN.disabled'));
      } else {
        await this.setupAccount(accountId);
      }
    } else if (codeExisted && !passwordExisted) {
      this.promptError(this.translate.instant('ACCOUNTS_LOGIN.duplicate_login'));
    } else {
      await this.loginAccountCall(weddingId, weddingPass, loginMethod);
    }
  }

  /**
   * Account Login cloud function call
   * @param weddingId Wedding ID
   * @param weddingPass Wedding Password
   * @param type Type
   */
  async loginAccountCall(weddingId: string, weddingPass: string, loginMethod: ShareMethod) {
    // let checkTrial = false;
    // if (this.accountsListService.getAvailableTrialJoin() === 1 || this.accountsListService.getAvailableTrialJoin() === 2) {
    //   checkTrial = true;
    // }
    const updateBy: UpdateBy = this.updateByService.getUpdateBy();
    await this.fns.httpsCallable('loginAccountCall')({
      weddingId,
      weddingPass,
      loginMethod,
      updateBy,
      isHybrid: this.platform.is('hybrid'),
      uid: this.userService.uid
    }).toPromise().then(async (loginResult: LoginResult) => {
      const result = loginResult?.result;
      if (result?.success && loginResult?.accountId) {
        await this.updateUserRtdb(loginResult.accountId)
        await this.setupAccount(loginResult.accountId);
        this.analyticsService.accountLogin(loginResult.accountId);
      } else {
        const errorList = [
          'disabled', 'invalid', 'error', 'expired',
          'trial_expired', 'app_access_only', 'web_access_only',
          'duplicate_login', 'duplicate_owner', 'duplicate_wedding_couple', 'anonymous', 'user_disabled'
        ];
        if (result.error && result.msg && errorList?.indexOf(result.msg) !== -1) {
          this.promptError(this.translate.instant('ACCOUNTS_LOGIN.' + result.msg));
        } else {
          this.promptError();
        }
      }
    }).catch((err: any) => {
      this.errorService.logError(err);
      this.promptError(this.translate.instant('MSG.action_error'));
    });
  }

  /**
   * Setup account
   * @param accountId Account ID
   */
  async setupAccount(accountId: string) {
    if (accountId) {
      const currentAccountId = await this.accountService.readAccountId();
      if (currentAccountId && currentAccountId !== accountId) {
        await this.accountService.logoutAccount(true);
        await this.functionService.delay(1000);
      }
      await this.accountService.setupAccount(accountId);
    } else {
      this.promptError();
    }
  }

  async updateUserRtdb(accountId: string) {
    if (accountId) {
      const userAccountsList = this.accountsListService.userAccountsList;
      const index = userAccountsList.findIndex((x: UserAccounts) => {
        return x.accountId === accountId;
      });
      if (index !== -1) {
        const userAccount = userAccountsList[index];
        await this.userRtdbService.updateUserAccountsRtdb(this.userService.uid, userAccount);
      }
    }
  }

  /**
   * Prompt error and go back to account list page once alert close
   * @param header alert header
   * @param msg alert message
   */
  async promptError(msg?: string) {
    if (!msg) {
      msg = this.translate.instant('ACCOUNTS_LOGIN.invalid');
    }
    const alert = await this.popupService.presentAlert(msg);
    alert.onDidDismiss().then(() => {
      this.goBack();
    });
  }

  /**
   * Go back accounts list page
   */
  goBack() {
    this.router.navigate(['/tabs-accounts/list'], { replaceUrl: true });
  }

}
